import React from 'react';
import { FaMoneyBillWave, FaWallet, FaUsers, FaCalendarCheck, FaChartLine, FaUserCheck, FaExclamationTriangle, FaUserTimes, FaChalkboardTeacher } from 'react-icons/fa';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';

export default function DashboardOverview({
  financialStats, studentStats, todayAttendance, teacherAttendance, revenueHistory, reconciliationHistory, attendanceTrend, topDefaulters, lowAttendanceStudents, formatCurrency
}) {
  return (
    <div className="dashboard-content">
      {/* Key Metrics Row */}
      <div className="metrics-grid">
        <div className="metric-card financial">
          <div className="metric-icon"><FaMoneyBillWave /></div>
          <div className="metric-info">
            <p className="metric-label">Monthly Revenue</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.totalRevenue || 0)}</h2>
            <p className="metric-subtext">{financialStats?.totalTransactions || 0} transactions</p>
          </div>
        </div>
        <div className="metric-card success">
          <div className="metric-icon"><FaWallet /></div>
          <div className="metric-info">
            <p className="metric-label">Today's Collection</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.todaysCollection || 0)}</h2>
            <p className="metric-subtext">Collected today</p>
          </div>
        </div>
        <div className="metric-card students">
          <div className="metric-icon"><FaUsers /></div>
          <div className="metric-info">
            <p className="metric-label">Active Students</p>
            <h2 className="metric-value">{studentStats?.activeStudents || 0}</h2>
            <p className="metric-subtext">Out of {studentStats?.totalStudents || 0} total</p>
          </div>
        </div>
        <div className="metric-card attendance">
          <div className="metric-icon"><FaCalendarCheck /></div>
          <div className="metric-info">
            <p className="metric-label">Today's Attendance</p>
            <h2 className="metric-value">{todayAttendance?.attendanceRate || 0}%</h2>
            <p className="metric-subtext">{todayAttendance?.presentCount || 0} / {todayAttendance?.totalStudents || 0} present</p>
          </div>
        </div>
      </div>
      {/* Charts Row 1 */}
      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-header">
            <h3><FaChartLine /> Revenue & Students Trend (Academic Session)</h3>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={reconciliationHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                <XAxis dataKey="month" stroke="#7f8c8d" />
                <YAxis stroke="#7f8c8d" />
                <YAxis yAxisId="right" orientation="right" stroke="#2ecc71" />
                <Tooltip formatter={(value, name) => name === 'students' ? value : formatCurrency(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#3498db" strokeWidth={3} dot={{ fill: '#3498db', r: 5 }} activeDot={{ r: 7 }} name="Revenue (₹)" />
                <Line type="monotone" dataKey="students" stroke="#2ecc71" strokeWidth={3} dot={{ fill: '#2ecc71', r: 5 }} activeDot={{ r: 7 }} name="Students" yAxisId="right" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaUserCheck /> Attendance Rate (Last 7 Days)</h3>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={attendanceTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                <XAxis dataKey="date" stroke="#7f8c8d" />
                <YAxis stroke="#7f8c8d" domain={[0, 100]} />
                <Tooltip formatter={value => `${value}%`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                <Legend />
                <Line type="monotone" dataKey="rate" stroke="#2ecc71" strokeWidth={3} dot={{ fill: '#2ecc71', r: 4 }} name="Attendance Rate (%)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Widgets Row */}
      <div className="widgets-row">
        <div className="widget-card">
          <div className="widget-header">
            <h3><FaExclamationTriangle /> Top Defaulters</h3>
          </div>
          <div className="widget-body">
            <div className="defaulters-list">
              {topDefaulters.length > 0 ? (
                topDefaulters.slice(0, 5).map((student, index) => (
                  <div key={student.rno} className="defaulter-item">
                    <div className="defaulter-info">
                      <span className="defaulter-rank">#{index + 1}</span>
                      <div className="defaulter-details">
                        <span className="defaulter-name">{student.name}</span>
                        <span className="defaulter-class">Class: {student.class}</span>
                      </div>
                    </div>
                    <span className="defaulter-amount">{formatCurrency(student.dues)}</span>
                  </div>
                ))
              ) : (
                <p className="no-data">No outstanding dues!</p>
              )}
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="widget-header">
            <h3><FaUserTimes /> Low Attendance Alert</h3>
          </div>
          <div className="widget-body">
            <div className="low-attendance-list">
              {lowAttendanceStudents.length > 0 ? (
                lowAttendanceStudents.slice(0, 5).map((student) => (
                  <div key={student.rno} className="attendance-item">
                    <div className="attendance-info">
                      <span className="attendance-name">{student.name}</span>
                      <span className="attendance-class">Class: {student.class}</span>
                    </div>
                    <div className="attendance-stats">
                      <span className={`attendance-percentage ${parseFloat(student.attendancePercentage) < 50 ? 'critical' : 'warning'}`}>{student.attendancePercentage}%</span>
                      <span className="attendance-days">{student.presentDays}/{student.totalDays} days</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">All students have good attendance!</p>
              )}
            </div>
          </div>
        </div>
        <div className="widget-card">
          <div className="widget-header">
            <h3><FaChalkboardTeacher /> Teacher Attendance Today</h3>
          </div>
          <div className="widget-body teacher-attendance">
            <div className="teacher-stats">
              <div className="stat-circle present">
                <FaUserCheck />
                <span className="stat-number">{teacherAttendance?.presentCount || 0}</span>
                <span className="stat-label">Present</span>
              </div>
              <div className="stat-circle absent">
                <FaUserTimes />
                <span className="stat-number">{teacherAttendance?.absentCount || 0}</span>
                <span className="stat-label">Absent</span>
              </div>
            </div>
            <div className="teacher-rate">
              <h2>{teacherAttendance?.attendanceRate || 0}%</h2>
              <p>Attendance Rate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
